import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { GALLERY } from "../data";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export const fileUrl = (path, width) =>
  width
    ? `${BACKEND}/api/files/${path}?w=${width}`
    : `${BACKEND}/api/files/${path}`;

// Known countries to deduplicate the home gallery preview by.
// Match is case-insensitive; the longest match wins (so "Czech Republic"
// is detected even though "Czech" is shorter).
const COUNTRY_PATTERNS = [
  ["czech republic", "czechia"],
  ["united kingdom", "england", "uk"],
  ["united states", "usa", "us"],
  ["netherlands"],
  ["belgium"],
  ["germany"],
  ["france"],
  ["italy"],
  ["spain"],
  ["portugal"],
  ["greece"],
  ["turkey"],
  ["romania"],
  ["hungary"],
  ["austria"],
  ["switzerland"],
  ["ireland"],
  ["denmark", "danemark"],
  ["sweden"],
  ["norway"],
  ["finland"],
  ["poland"],
  ["bulgaria"],
  ["serbia"],
  ["croatia"],
  ["slovenia"],
  ["slovakia"],
  ["estonia"],
  ["latvia"],
  ["lithuania"],
  ["peru"],
  ["mexico"],
  ["brazil"],
  ["argentina"],
  ["chile"],
  ["colombia"],
  ["india"],
  ["thailand"],
  ["vietnam"],
  ["indonesia"],
  ["bali"],
  ["japan"],
  ["china"],
  ["morocco"],
  ["egypt"],
  ["israel"],
  ["uae", "dubai"],
];

// Returns a canonical country key for a "place" caption, or null if none recognized.
const detectCountry = (place) => {
  if (!place) return null;
  const text = ` ${place.toLowerCase().replace(/[.,]/g, " ")} `;
  // Sort patterns by length desc so multi-word matches win.
  const flat = COUNTRY_PATTERNS.flatMap((aliases) =>
    aliases.map((a) => ({ alias: a, key: aliases[0] }))
  ).sort((a, b) => b.alias.length - a.alias.length);
  for (const { alias, key } of flat) {
    if (text.includes(` ${alias} `)) return key;
  }
  return null;
};

export const Gallery = () => {
  const [items, setItems] = useState(
    GALLERY.slice(0, 10).map((url) => ({ url, rotation: 0 }))
  );

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const albumsRes = await fetch(`${API}/albums`).then((r) => r.json());
        const moments = Array.isArray(albumsRes)
          ? albumsRes.find((a) => a.name === "Moments")
          : null;
        const url = moments
          ? `${API}/photos?limit=200&album_id=${moments.id}`
          : `${API}/photos?limit=200`;
        const data = await fetch(url).then((r) => r.json());
        if (!active || !Array.isArray(data) || data.length === 0) return;
        // Pick at most one photo per detected country (max 10 total).
        // Photos with an unrecognized/empty caption are kept as a backup
        // pool and only used after we exhaust known-country photos.
        const seen = new Set();
        const picked = [];
        const backup = [];
        for (const p of data) {
          const country = detectCountry(p.place);
          if (country) {
            if (seen.has(country)) continue;
            seen.add(country);
            picked.push({
              url: fileUrl(p.image_path, 600),
              rotation: p.rotation || 0,
            });
          } else {
            backup.push({
              url: fileUrl(p.image_path, 600),
              rotation: p.rotation || 0,
            });
          }
          if (picked.length >= 10) break;
        }
        // Pad with backup photos if we couldn't reach 10 distinct countries.
        let i = 0;
        while (picked.length < 10 && i < backup.length) {
          picked.push(backup[i]);
          i++;
        }
        if (picked.length > 0) setItems(picked);
      } catch {
        /* silent */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="jurnal"
      data-testid="gallery-section"
      className="relative z-10 py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mb-16 text-center"
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D6B56D] mb-6">
            Travel journal
          </p>
          <h2 className="font-heading font-light text-5xl md:text-6xl text-[#EBE4D8]">
            Moments <span className="italic text-[#D6B56D]">captured</span>
          </h2>
        </motion.div>

        <div
          data-testid="gallery-grid"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4"
        >
          {items.slice(0, 10).map((item, i) => {
            const r = item.rotation || 0;
            const isSide = r === 90 || r === 270;
            return (
              <motion.div
                key={`${item.url}-${i}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: (i % 5) * 0.05 }}
                data-testid={`gallery-card-${i}`}
                className="group relative aspect-square rounded-xl overflow-hidden border border-[#2C223A] hover:border-[#D6B56D]/50 transition-colors duration-500"
              >
                <img
                  src={item.url}
                  alt=""
                  loading={i < 5 ? "eager" : "lazy"}
                  decoding="async"
                  style={r ? { transform: `rotate(${r}deg)` } : undefined}
                  className={`absolute inset-0 h-full w-full ${
                    isSide ? "object-contain" : "object-cover"
                  } transition-transform duration-700 group-hover:scale-110`}
                />
                <div className="absolute inset-0 bg-[#06040A]/20 group-hover:bg-transparent transition-colors duration-500" />
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 flex justify-center">
          <Link
            to="/moments"
            data-testid="see-all-moments-button"
            className="group inline-flex items-center gap-3 px-9 py-3.5 rounded-full border border-[#D6B56D]/60 text-[#EBE4D8] text-sm tracking-widest uppercase hover:bg-[#D6B56D]/10 hover:border-[#D6B56D] transition-all duration-300"
          >
            See all moments
            <ArrowRight
              size={16}
              className="text-[#D6B56D] group-hover:translate-x-1 transition-transform duration-300"
            />
          </Link>
        </div>
      </div>
    </section>
  );
};
